import { useMemo } from 'react';

/**
 * Custom hook for AI chart data context and patient details
 * Provides comprehensive chart data context and patient details for AI analysis
 */
export const useAIChartAndPatientContext = () => {
  return useMemo(() => {
    // CRITICAL: Chart Data Context for AI Analysis
    const chartDataContext = {
      description: "Chart data provides time-based analysis of all card data with multiple view options",
      availableViews: {
        daily: {
          label: "Daily View",
          description: "Shows data broken down by individual days",
          timeFormat: "YYYY-MM-DD",
          aggregation: "day",
          bestFor: "Detailed day-to-day analysis, identifying daily patterns"
        },
        weekly: {
          label: "Weekly View", 
          description: "Shows data aggregated by weeks",
          timeFormat: "Week of YYYY-MM-DD",
          aggregation: "week",
          bestFor: "Weekly trends, comparing week-over-week performance"
        },
        sevenDay: {
          label: "7-Day Rolling",
          description: "Shows 7-day rolling periods for trend analysis",
          timeFormat: "7-day period ending YYYY-MM-DD",
          aggregation: "7-day-rolling",
          bestFor: "Smoothed trend analysis, reducing daily volatility"
        },
        thirtyDay: {
          label: "30-Day Rolling",
          description: "Shows 30-day rolling periods for longer trend analysis", 
          timeFormat: "30-day period ending YYYY-MM-DD",
          aggregation: "30-day-rolling",
          bestFor: "Monthly trend analysis, seasonal patterns"
        },
        monthly: {
          label: "Monthly View",
          description: "Shows data aggregated by calendar months",
          timeFormat: "MMMM YYYY",
          aggregation: "month",
          bestFor: "Month-over-month comparisons, seasonal analysis"
        },
        quarterly: {
          label: "Quarterly View",
          description: "Shows data aggregated by quarters",
          timeFormat: "Q# YYYY",
          aggregation: "quarter", 
          bestFor: "Quarterly business analysis, long-term trends"
        },
        yearly: {
          label: "Yearly View",
          description: "Shows data aggregated by years",
          timeFormat: "YYYY",
          aggregation: "year",
          bestFor: "Year-over-year analysis, long-term strategic insights"
        }
      },
      
      chartDataStructure: {
        description: "Each chart data point contains comprehensive information",
        dataPoint: {
          x: "Date/time identifier in appropriate format",
          y: "Primary value (count of transfers/events)",
          z: "Display label (formatted date/period name)",
          total: "Total count for the period",
          percentage: "Percentage relative to census or other base",
          facilityIds: "Array of facility IDs involved in this period",
          facilityWiseData: "Breakdown of data by individual facilities",
          censusData: "Census information for percentage calculations"
        },
        
        aggregationLogic: {
          daily: "Each point represents one calendar day",
          weekly: "Each point represents one calendar week (Sunday-Saturday)",
          monthly: "Each point represents one calendar month",
          rolling: "Each point represents a rolling period (7 or 30 days)",
          quarterly: "Each point represents a 3-month quarter",
          yearly: "Each point represents a calendar year"
        }
      },
      
      // CRITICAL: How AI should interpret chart questions
      chartQuestionHandling: {
        trendQuestions: {
          examples: [
            "What's the trend over the last month?",
            "How has this changed week over week?", 
            "Show me daily patterns",
            "What's the monthly trend?"
          ],
          approach: "Analyze the appropriate time aggregation and describe patterns, peaks, valleys, and overall direction"
        },
        
        comparisonQuestions: {
          examples: [
            "Compare this month to last month",
            "How does this week compare to previous weeks?",
            "What's different about weekends vs weekdays?"
          ],
          approach: "Compare values across time periods and highlight significant differences"
        },
        
        patternQuestions: {
          examples: [
            "Are there any patterns in the data?",
            "What days are busiest?",
            "When do we see spikes?"
          ],
          approach: "Identify recurring patterns, cyclical behavior, and anomalies"
        },
        
        specificTimeQuestions: {
          examples: [
            "What happened on March 15th?",
            "Show me data for last Tuesday",
            "How many transfers were there in January?"
          ],
          approach: "Find specific time periods and provide exact values with context"
        }
      },
      
      // Mock chart data structure for AI understanding
      sampleChartData: {
        daily: [
          {
            x: "2025-01-15",
            y: 12,
            z: "Jan 15",
            total: 12,
            percentage: 15.2,
            facilityIds: ["fac1", "fac2"],
            facilityWiseData: { "fac1": 8, "fac2": 4 }
          }
        ],
        weekly: [
          {
            x: "2025-01-13",
            y: 45,
            z: "Week of Jan 13",
            total: 45,
            percentage: 18.7,
            facilityIds: ["fac1", "fac2", "fac3"],
            facilityWiseData: { "fac1": 20, "fac2": 15, "fac3": 10 }
          }
        ],
        monthly: [
          {
            x: "2025-01-01",
            y: 180,
            z: "January 2025",
            total: 180,
            percentage: 22.1,
            facilityIds: ["fac1", "fac2", "fac3"],
            facilityWiseData: { "fac1": 85, "fac2": 60, "fac3": 35 }
          }
        ]
      }
    };

    // CRITICAL: Patient Residence/Details Context for AI Analysis
    const patientDetailsContext = {
      description: "Patient details provide individual-level information for each transfer/event",
      
      availableFields: {
        basicInfo: {
          patientId: "Unique patient identifier",
          facilityId: "Source facility ID",
          facilityName: "Source facility name",
          dateOfADT: "Date of admission/discharge/transfer",
          timeOfADT: "Time of admission/discharge/transfer"
        },
        
        demographicInfo: {
          age: "Patient age at time of transfer",
          gender: "Patient gender",
          insurance: "Insurance type/payer source",
          insuranceId: "Insurance provider ID"
        },
        
        clinicalInfo: {
          diagnosis: "Primary diagnosis codes",
          dxIds: "Array of diagnosis IDs", 
          wasAdmitted: "Boolean - true for DC (admitted), false for ER (not admitted)",
          wasReturned: "Boolean - whether patient returned within timeframe",
          reHospitalization: "Boolean - true for re-hospitalizations, false for new"
        },
        
        locationInfo: {
          floorId: "Floor/unit ID where patient was located",
          floorName: "Floor/unit name",
          roomNumber: "Room number if available",
          bedNumber: "Bed number if available"
        },
        
        careTeamInfo: {
          doctorId: "Attending doctor ID",
          doctorName: "Attending doctor name",
          nurseId: "Primary nurse ID", 
          nurseName: "Primary nurse name",
          shiftName: "Shift during which transfer occurred (Morning/Evening/Night)"
        },
        
        transferInfo: {
          hospitalId: "Destination hospital ID (if applicable)",
          hospitalName: "Destination hospital name",
          transferType: "planned/unplanned classification",
          transferReason: "Reason for transfer",
          isOutgoing: "Boolean - true for outgoing transfers"
        },
        
        customQuestions: {
          description: "Custom facility-specific questions and answers",
          structure: "Array of question-answer pairs with question metadata"
        }
      },
      
      // CRITICAL: How AI should handle patient detail questions
      patientDetailQuestionHandling: {
        countQuestions: {
          examples: [
            "How many patients from Floor 3?",
            "How many Medicare patients?",
            "How many patients with Dr. Smith?"
          ],
          approach: "Filter patient list by specified criteria and return exact count"
        },
        
        listQuestions: {
          examples: [
            "Show me patients from last Tuesday",
            "List all patients with pneumonia",
            "Who are the patients that returned?"
          ],
          approach: "Filter and present relevant patient information in organized format"
        },
        
        analysisQuestions: {
          examples: [
            "What's the average age of transferred patients?",
            "Which floor has the most transfers?",
            "What are the most common diagnoses?"
          ],
          approach: "Analyze patient data to provide statistical insights and summaries"
        },
        
        relationshipQuestions: {
          examples: [
            "Do older patients return more often?",
            "Which doctors have the most transfers?",
            "Is there a pattern by insurance type?"
          ],
          approach: "Analyze relationships between different patient attributes"
        }
      },
      
      // Sample patient data structure for AI understanding
      samplePatientData: [
        {
          patientId: "P12345",
          facilityName: "Main Campus",
          dateOfADT: "2025-01-15",
          timeOfADT: "14:30",
          age: 78,
          gender: "Female",
          insurance: { label: "Medicare", id: "medicare" },
          diagnosis: [{ label: "Pneumonia", code: "J18.9" }],
          wasAdmitted: true,
          wasReturned: false,
          reHospitalization: false,
          floorName: "ICU",
          doctorName: "Dr. Johnson",
          nurseName: "Nurse Williams",
          shiftName: "Evening",
          hospitalName: "City General Hospital",
          transferType: "unplanned"
        }
      ]
    };

    // CRITICAL: Comprehensive Chart and Patient Details Integration
    const chartAndDetailsIntegration = {
      description: "How chart data and patient details work together for comprehensive analysis",
      
      // Chart data provides the "what and when", patient details provide the "who and why"
      analysisApproach: {
        timeBasedAnalysis: {
          description: "Use chart data to identify patterns over time",
          process: [
            "1. Identify time periods of interest from chart data",
            "2. Drill down to patient details for those periods",
            "3. Analyze patient characteristics during peak/low periods",
            "4. Provide insights combining temporal and individual data"
          ]
        },
        
        patientDrivenAnalysis: {
          description: "Use patient details to understand chart patterns",
          process: [
            "1. Analyze patient characteristics and patterns",
            "2. Group patients by relevant attributes",
            "3. Show how these groups contribute to chart trends",
            "4. Explain chart patterns through patient demographics"
          ]
        }
      },
      
      // Common integrated questions and how to handle them
      integratedQuestionHandling: {
        trendWithDetails: {
          examples: [
            "Why did discharges spike last Tuesday?",
            "What caused the increase in night shift transfers?",
            "Who are the patients driving the Medicare trend?"
          ],
          approach: "Combine chart trend identification with patient detail analysis to explain causation"
        },
        
        patternExplanation: {
          examples: [
            "Why do we see more transfers on Mondays?",
            "What's causing the seasonal pattern in readmissions?",
            "Why is Floor 3 showing increased activity?"
          ],
          approach: "Use patient details to explain chart patterns and provide actionable insights"
        },
        
        comparativeAnalysis: {
          examples: [
            "How do patient characteristics differ between high and low activity periods?",
            "What's different about patients transferred during night shifts?",
            "Compare patient profiles for this month vs last month"
          ],
          approach: "Compare patient details across different time periods or chart segments"
        }
      },
      
      // Card-specific chart and patient detail combinations
      cardSpecificIntegration: {
        hospitalizations: {
          chartFocus: "Track new vs re-hospitalization trends over time",
          patientDetailsFocus: "Analyze patient characteristics that lead to readmissions",
          combinedInsights: "Identify patient risk factors and temporal patterns for readmissions"
        },
        
        dcer: {
          chartFocus: "Monitor admission rates and ER utilization trends",
          patientDetailsFocus: "Understand patient acuity and admission criteria",
          combinedInsights: "Optimize admission processes and capacity management"
        },
        
        shifts: {
          chartFocus: "Track workload distribution across shifts over time",
          patientDetailsFocus: "Analyze patient acuity and transfer reasons by shift",
          combinedInsights: "Optimize staffing and identify workflow improvements"
        },
        
        insurance: {
          chartFocus: "Monitor payer mix trends and changes over time",
          patientDetailsFocus: "Analyze patient outcomes and characteristics by insurance type",
          combinedInsights: "Understand payer-specific patterns and financial implications"
        },
        
        floors: {
          chartFocus: "Track unit-specific activity and trends",
          patientDetailsFocus: "Analyze patient acuity and outcomes by floor/unit",
          combinedInsights: "Optimize unit capacity and identify care patterns"
        },
        
        doctors: {
          chartFocus: "Monitor physician-specific transfer patterns over time",
          patientDetailsFocus: "Analyze patient outcomes and characteristics by physician",
          combinedInsights: "Identify best practices and improvement opportunities"
        },
        
        diagnosis: {
          chartFocus: "Track diagnosis-specific trends and seasonal patterns",
          patientDetailsFocus: "Analyze patient outcomes and characteristics by diagnosis",
          combinedInsights: "Understand disease patterns and care effectiveness"
        }
      }
    };

    return {
      chartDataContext,
      patientDetailsContext,
      chartAndDetailsIntegration
    };
  }, []);
};

export default useAIChartAndPatientContext;
